import React, { type ComponentProps } from 'react';
import { LucideIcon, Settings } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/utils';

interface AdminHeaderProps {
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  title?: string;
  description?: string | React.ReactNode;
  rightContent?: React.ReactNode;
  /** Icon-only settings control, pinned to the top-right corner. */
  settings?: React.ReactNode;
  className?: string;
  // Color theme variants
  variant?: 'violet' | 'purple' | 'blue' | 'cyan' | 'emerald' | 'indigo';
  children?: React.ReactNode;
}

export function AdminHeaderSettingsButton({ className, ...props }: ComponentProps<typeof Button>) {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label="Settings"
      title="Settings"
      className={cn(
        'bg-white/50 dark:bg-slate-700/50 border-blue-200 dark:border-slate-600 hover:bg-white/80 dark:hover:bg-slate-600/80',
        className
      )}
      {...props}
    >
      <Settings className="size-4" />
    </Button>
  );
}

const variantStyles = {
  violet: {
    background:
      'from-violet-50 via-purple-50 to-fuchsia-50 dark:from-violet-950/40 dark:via-purple-950/30 dark:to-fuchsia-950/20',
    border: 'border-violet-100 dark:border-violet-800/40',
    circle: 'from-violet-200/20 to-purple-200/20 dark:from-violet-700/10 dark:to-purple-700/10',
    iconBg: 'from-violet-500 to-purple-600',
  },
  purple: {
    background:
      'from-purple-50 via-violet-50 to-pink-50 dark:from-purple-950/40 dark:via-violet-950/30 dark:to-pink-950/20',
    border: 'border-purple-100 dark:border-purple-800/40',
    circle: 'from-purple-200/20 to-pink-200/20 dark:from-purple-700/10 dark:to-pink-700/10',
    iconBg: 'from-purple-500 to-pink-600',
  },
  blue: {
    background:
      'from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/40 dark:via-indigo-950/30 dark:to-purple-950/20',
    border: 'border-blue-100 dark:border-blue-800/40',
    circle: 'from-blue-200/20 to-purple-200/20 dark:from-blue-700/10 dark:to-purple-700/10',
    iconBg: 'from-blue-500 to-purple-600',
  },
  cyan: {
    background:
      'from-cyan-50 via-blue-50 to-indigo-50 dark:from-cyan-950/40 dark:via-blue-950/30 dark:to-indigo-950/20',
    border: 'border-cyan-100 dark:border-cyan-800/40',
    circle: 'from-cyan-200/20 to-blue-200/20 dark:from-cyan-700/10 dark:to-blue-700/10',
    iconBg: 'from-cyan-500 to-blue-600',
  },
  emerald: {
    background:
      'from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-cyan-950/20',
    border: 'border-emerald-100 dark:border-emerald-800/40',
    circle: 'from-emerald-200/20 to-teal-200/20 dark:from-emerald-700/10 dark:to-teal-700/10',
    iconBg: 'from-emerald-500 to-teal-600',
  },
  indigo: {
    background:
      'from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-950/40 dark:via-purple-950/30 dark:to-pink-950/20',
    border: 'border-indigo-100 dark:border-indigo-800/40',
    circle: 'from-indigo-200/20 to-purple-200/20 dark:from-indigo-700/10 dark:to-purple-700/10',
    iconBg: 'from-indigo-500 to-purple-600',
  },
};

export function AdminHeader({
  icon: Icon,
  title,
  description,
  rightContent,
  settings,
  className,
  variant = 'violet',
  children,
}: AdminHeaderProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-2xl border bg-gradient-to-br p-8',
        styles.background,
        styles.border,
        className
      )}
    >
      {/* Decorative circle */}
      <div className="absolute top-0 right-0 transform translate-x-16 -translate-y-16">
        <div className={cn('w-64 h-64 rounded-full bg-gradient-to-br', styles.circle)} />
      </div>

      {/* Mobile: settings pinned to the top-right corner */}
      {settings && <div className="absolute top-4 right-4 z-10 md:hidden">{settings}</div>}

      <div className="relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            {children ? (
              children
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  {Icon && (
                    <div
                      className={cn('p-2 rounded-xl bg-gradient-to-br text-white', styles.iconBg)}
                    >
                      <Icon className="size-6" />
                    </div>
                  )}
                  {title && (
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                      {title}
                    </h1>
                  )}
                </div>
                <div className="text-slate-600 dark:text-slate-300 max-w-2xl">{description}</div>
              </>
            )}
          </div>
          {/* Desktop: settings first from the right, then other right-side elements */}
          {(rightContent || settings) && (
            <div className="hidden md:flex items-center gap-3 shrink-0 flex-row-reverse">
              {settings}
              {rightContent}
            </div>
          )}
        </div>
        {/* Mobile rightContent below description */}
        {rightContent && <div className="md:hidden mt-4 w-full">{rightContent}</div>}
      </div>
    </div>
  );
}

// Wrapper component that includes the standard container and spacing
interface AdminPageLayoutProps {
  header: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function AdminPageLayout({ header, children, className }: AdminPageLayoutProps) {
  return (
    <div className={cn('mx-auto w-full min-w-0 max-w-6xl space-y-6 p-6', className)}>
      {header}
      {children}
    </div>
  );
}
